import { z } from "zod";

/**
 * In-app support (PLT-09; `crmarr/templates` in-app-support-panel.md): the
 * round ? on every signed-in page. Ask the docs shelf (or search it), or reach a
 * person through one short form — a ticket, a feature request or a bug report —
 * that already knows who, which page, the version, the browser and the last
 * failed request id.
 *
 * The lists here are the handler's and the panel's both, so a kind, an
 * area or a severity one side does not know fails `tsc` rather than a
 * Send button (qa-battery.md §1.1 rule 2).
 */
export const SUPPORT_KINDS = ["ticket", "feature", "bug"] as const;
export type SupportKind = (typeof SUPPORT_KINDS)[number];

/** The product's own sections, in the words of the rail; `other` last. */
export const SUPPORT_AREAS = ["training", "account", "sign_in", "people", "audit", "docs", "other"] as const;
export type SupportArea = (typeof SUPPORT_AREAS)[number];

export const SUPPORT_AREA_LABEL: Record<SupportArea, string> = {
  training: "My training",
  account: "Account and security",
  sign_in: "Signing in",
  people: "Users and roles",
  audit: "Audit trail",
  docs: "Documentation",
  other: "Something else",
};

/** A ticket asks no severity; a bug and a feature each have their own scale. */
export const SUPPORT_SEVERITIES = {
  bug: ["blocked", "workaround", "cosmetic"],
  feature: ["essential", "saves_time", "nice_to_have"],
  ticket: [],
} as const satisfies Record<SupportKind, readonly string[]>;
export type SupportSeverity = (typeof SUPPORT_SEVERITIES)[keyof typeof SUPPORT_SEVERITIES][number];

/** The pre-selected severity: the middle of the scale, never the alarm (the template's owner pass). */
export const DEFAULT_SEVERITY: Record<SupportKind, SupportSeverity | null> = { bug: "workaround", feature: "saves_time", ticket: null };

export const SUPPORT_SEVERITY_LABEL: Record<SupportSeverity, string> = {
  blocked: "I'm blocked",
  workaround: "It's wrong, but I can work around it",
  cosmetic: "It looks wrong, nothing more",
  essential: "I can't do my work without it",
  saves_time: "It would save real time",
  nice_to_have: "Nice to have",
};

/**
 * `open` is all there is today: nothing moves a request on yet, and the
 * vocabulary is settled here so the next step (a reply, an operator's
 * queue — the desk hand-off is the owner's decision) adds no migration.
 */
export type SupportStatus = "open" | "in_progress" | "waiting" | "closed";

export const CreateSupportRequestSchema = z
  .object({
    kind: z.enum(SUPPORT_KINDS),
    area: z.enum(SUPPORT_AREAS),
    severity: z.string().nullable().optional(),
    body: z.string().trim().min(1).max(4000),
    expected: z.string().trim().max(2000).nullable().optional(),
    page: z.object({ title: z.string().trim().max(200).nullable().optional(), path: z.string().trim().max(500).nullable().optional() }).optional(),
    appVersion: z.string().trim().max(40).nullable().optional(),
    userAgent: z.string().trim().max(400).nullable().optional(),
    lastRequestId: z.string().trim().max(100).nullable().optional(),
  })
  .strict()
  // A severity must belong to its kind's scale; a ticket has none.
  .refine((b) => ((SUPPORT_SEVERITIES[b.kind] as readonly string[]).length === 0 ? !b.severity : (SUPPORT_SEVERITIES[b.kind] as readonly string[]).includes(b.severity ?? "")), { message: "severity does not belong to that kind", path: ["severity"] });
export type CreateSupportRequest = z.infer<typeof CreateSupportRequestSchema>;

export interface SupportRequest {
  id: string;
  /** Quotable within the tenant: "request 104". Per tenant, never a cross-tenant sequence (TEN-09). */
  number: number;
  kind: SupportKind;
  area: SupportArea;
  severity: SupportSeverity | null;
  page: { title: string | null; path: string | null };
  body: string;
  expected: string | null;
  status: SupportStatus;
  /**
   * Where it went (ADR-0022): `handed_off` once it is a case in the support desk's shared Connect Cases domain, with
   * the reference an agent quotes; `pending` until then — an environment without the hand-off, or a failed attempt
   * waiting for the next one. The request is recorded either way.
   */
  handoff: { status: "pending" | "handed_off"; caseReference: string | null };
  requester: { email: string; name: string };
  createdAt: string;
}

export interface SupportRequestsResponse {
  requests: SupportRequest[];
}

/**
 * A request as a tenant admin sees it in the tenant's whole list (`GET /v1/support/requests?all=true`): metadata
 * only — who sent it, when, what kind, the severity, the area and the page's path, the status, the number and the
 * case reference. Never the words: no `body`, no `expected`, and no page title, because the person can type over
 * the title in the form. The owner's decision on gairm/lms#220, 2026-09-23 — the text is visible only to the person
 * who sent it (*My requests*) and to the support desk. The handler selects none of it, so the words never leave
 * the database on this path; a portal that reached for `body` here fails `tsc`.
 */
export interface SupportRequestSummary {
  id: string;
  number: number;
  kind: SupportKind;
  area: SupportArea;
  severity: SupportSeverity | null;
  page: { path: string | null };
  status: SupportStatus;
  handoff: SupportRequest["handoff"];
  requester: SupportRequest["requester"];
  createdAt: string;
}

export interface SupportRequestSummariesResponse {
  requests: SupportRequestSummary[];
}

export const AskDocsSchema = z.object({ question: z.string().trim().min(3).max(500) }).strict();
export type AskDocsRequest = z.infer<typeof AskDocsSchema>;

/** Ask: an answer from the docs shelf, naming the page (and heading) it rests on — or the fixed sentence that it is not covered. */
export interface DocsAnswer {
  answer: string;
  answered: boolean;
  citations: Array<{ slug: string; title: string; heading?: { text: string; id: string } }>;
  /** Questions left today for this tenant. */
  remaining: number;
}
